import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { StaffLoginRequest } from '@naya/shared'
import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import type { StaffLoginResponse, StaffMe } from '../../owner/types.js'

export const staffMeKey = ['staff', 'me'] as const

/**
 * Current staff session — wraps GET /api/staff/me. A 401 resolves to `null`
 * (the StaffRoute gate redirects on null); any other failure propagates.
 * Staff is part of the owner-side app from a code-organization standpoint, so
 * it reuses `ownerApi` rather than a separate client.
 */
export function useStaffSession() {
  return useQuery<StaffMe | null>({
    queryKey: staffMeKey,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await ownerApi.get<StaffMe>('/staff/me')
      } catch (err) {
        if (err instanceof OwnerApiError && err.status === 401) return null
        throw err
      }
    },
  })
}

export function useStaffLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: StaffLoginRequest) =>
      ownerApi.post<StaffLoginResponse>('/staff/login', input),
    // Await the /me refetch before resolving so StaffRoute reads a populated
    // session cache when the page navigates — otherwise it flashes /staff/login.
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: staffMeKey })
    },
  })
}

export function useStaffLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ownerApi.post<{ ok: true }>('/staff/logout'),
    onSuccess: () => {
      qc.clear()
    },
  })
}
